/**
 * Sprint 208A — 3-in-1 Parent Console (loaded via magic link).
 *
 * One page, three actions:
 *   1. Approve teen's app access (toggle)
 *   2. Countersign HiBFF Commitment Charter (typed signature)
 *   3. Upgrade household to Premium (STUBBED — no Stripe call; surfaces a
 *      premium confirmation modal on success per Sprint 208A scope lock 2c)
 *
 * Loaded at /parent-console/:token — no auth required (the token itself
 * is the authentication; 7-day TTL is enforced server-side).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, FileText, CreditCard, Check, Loader2, AlertTriangle, Sparkles, X } from 'lucide-react';
import { apiClient } from '../../api/client';

const ParentConsole = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [approving, setApproving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const [typedSignature, setTypedSignature] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('premium_monthly');
  const [upgradeResult, setUpgradeResult] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.requestPublic(`/api/onboarding/console/${token}`);
      setState(data);
      if (data?.charter?.parent_signature) setTypedSignature(data.charter.parent_signature);
    } catch (e) {
      const msg = e?.message || 'Failed to load console.';
      setError(msg.includes('expired') ? 'This invite link has expired. Ask your parent for a fresh link.' : msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const toggleApprove = async (approved) => {
    setApproving(true);
    try {
      await apiClient.requestPublic(`/api/onboarding/console/${token}/approve`, {
        method: 'POST', body: JSON.stringify({ approved }),
      });
      await reload();
    } catch (e) { setError(e?.message || 'Approve failed.'); }
    finally { setApproving(false); }
  };

  const countersign = async () => {
    if (typedSignature.trim().length < 2) {
      setError('Please type your full name as your signature.');
      return;
    }
    setSigning(true);
    setError(null);
    try {
      await apiClient.requestPublic(`/api/onboarding/console/${token}/countersign`, {
        method: 'POST', body: JSON.stringify({ typed_signature: typedSignature.trim() }),
      });
      await reload();
    } catch (e) { setError(e?.message || 'Countersign failed.'); }
    finally { setSigning(false); }
  };

  const stubbedUpgrade = async () => {
    setUpgrading(true);
    setError(null);
    try {
      const res = await apiClient.requestPublic(`/api/onboarding/console/${token}/upgrade`, {
        method: 'POST',
        body: JSON.stringify({
          plan: selectedPlan,
          typed_signature: typedSignature.trim() || undefined,
        }),
      });
      setUpgradeResult(res);
      await reload();
    } catch (e) { setError(e?.message || 'Upgrade failed.'); }
    finally { setUpgrading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error && !state) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-red-50 via-white to-pink-50">
        <div className="max-w-md text-center" data-testid="parent-console-error">
          <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">We couldn&apos;t load this link</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const approval = state?.approval;
  const charterSigned = !!state?.charter?.parent_signature;
  const isPremium = state?.subscription?.current_tier === 'premium';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
      <div className="max-w-3xl mx-auto" data-testid="parent-console">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <Shield size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Parent Console</h1>
          {state?.teen ? (
            <p className="text-sm text-gray-600 mt-1">
              <strong>{state.teen.name || 'Your teen'}</strong> has joined HiBFF on the Free Plan.
              Confirm access, countersign the charter, and (optionally) upgrade — all in one go.
            </p>
          ) : (
            <p className="text-sm text-gray-600 mt-1">Complete the three steps below to activate your family on HiBFF.</p>
          )}
        </div>

        {/* Section 1 — Approve */}
        <section className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 mb-5" data-testid="console-section-approve">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Check size={20} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-gray-900">1. Approve app access</h2>
              <p className="text-xs text-gray-500">Greenlight your teen to use HiBFF — you can revoke this any time from the Parent Dashboard.</p>
            </div>
            {approval === 'approved' && <span className="text-xs font-bold text-green-600 px-2 py-0.5 bg-green-100 rounded-full">Approved</span>}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => toggleApprove(true)}
              disabled={approving}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                approval === 'approved'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700'
              }`}
              data-testid="console-approve-btn"
            >
              {approving ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Agree — give access'}
            </button>
            <button
              onClick={() => toggleApprove(false)}
              disabled={approving}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                approval === 'declined'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700'
              }`}
              data-testid="console-decline-btn"
            >Decline</button>
          </div>
        </section>

        {/* Section 2 — Countersign Charter */}
        <section className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 mb-5" data-testid="console-section-charter">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText size={20} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-gray-900">2. Countersign the Commitment Charter</h2>
              <p className="text-xs text-gray-500">Read and accept the family-level rules for HiBFF.</p>
            </div>
            {charterSigned && <span className="text-xs font-bold text-purple-600 px-2 py-0.5 bg-purple-100 rounded-full">Signed</span>}
          </div>
          <div className="bg-gradient-to-br from-amber-50 via-white to-pink-50 border border-amber-200 rounded-xl p-4 text-sm leading-relaxed text-gray-700 mb-4 max-h-44 overflow-y-auto" data-testid="console-charter-text">
            {state?.charter_text}
          </div>
          <label className="block mb-1">
            <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Parent digital signature</span>
            <input
              type="text"
              value={typedSignature}
              onChange={e => setTypedSignature(e.target.value)}
              placeholder="Type your full name"
              className="mt-1 w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none text-xl"
              style={{ fontFamily: 'Caveat, "Brush Script MT", cursive', fontStyle: 'italic' }}
              data-testid="console-signature-input"
              disabled={charterSigned}
            />
          </label>
          <p className="text-[11px] text-gray-400 mb-4">Typing your name is your binding digital signature on this charter.</p>
          {!charterSigned && (
            <button
              onClick={countersign}
              disabled={signing}
              className="w-full py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-sm rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="console-countersign-btn"
            >
              {signing ? <Loader2 size={16} className="animate-spin" /> : 'Countersign Charter'}
            </button>
          )}
        </section>

        {/* Section 3 — Upgrade */}
        <section className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 mb-5" data-testid="console-section-upgrade">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <CreditCard size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-gray-900">3. Upgrade to Premium <span className="text-xs font-normal text-gray-400">(optional)</span></h2>
              <p className="text-xs text-gray-500">Unlock unlimited AvA practice, all 20 challenges, and the family Vault.</p>
            </div>
            {isPremium && <span className="text-xs font-bold text-amber-700 px-2 py-0.5 bg-amber-100 rounded-full">Premium active</span>}
          </div>
          {!isPremium ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => setSelectedPlan('premium_monthly')}
                  className={`p-4 border-2 rounded-xl text-left transition-all ${selectedPlan === 'premium_monthly' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
                  data-testid="console-plan-monthly"
                >
                  <p className="text-xs font-semibold uppercase text-amber-700">Monthly</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">$14.99 <span className="text-xs font-normal text-gray-500">/ mo</span></p>
                  <p className="text-[11px] text-gray-500 mt-1">Cancel anytime</p>
                </button>
                <button
                  onClick={() => setSelectedPlan('premium_annual')}
                  className={`p-4 border-2 rounded-xl text-left transition-all ${selectedPlan === 'premium_annual' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
                  data-testid="console-plan-annual"
                >
                  <p className="text-xs font-semibold uppercase text-amber-700">Annual <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px]">Save 17%</span></p>
                  <p className="text-xl font-bold text-gray-900 mt-1">$149 <span className="text-xs font-normal text-gray-500">/ yr</span></p>
                  <p className="text-[11px] text-gray-500 mt-1">~$12.42 / mo</p>
                </button>
              </div>
              <button
                onClick={stubbedUpgrade}
                disabled={upgrading}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="console-upgrade-btn"
              >
                {upgrading ? <Loader2 size={16} className="animate-spin" /> : <><CreditCard size={16} /> Upgrade now</>}
              </button>
              <p className="text-[11px] text-gray-400 text-center mt-3">
                <strong>Sprint 208A:</strong> Stripe checkout is stubbed for testing — clicking Upgrade simulates the household upgrade without a payment call.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-700">Your family is on the Premium tier. 🎉</p>
          )}
        </section>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 mb-4" data-testid="console-error">{error}</div>
        )}

        {/* Premium confirmation modal */}
        {upgradeResult && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="console-upgrade-modal">
            <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center relative">
              <button
                onClick={() => setUpgradeResult(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
                aria-label="Close"
              ><X size={18} /></button>
              <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center">
                <Sparkles size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">You&apos;re on Premium</h3>
              <p className="text-sm text-gray-600 mb-4">
                Plan: <strong>{upgradeResult.plan?.replace('_', ' ')}</strong><br />
                Subscription ID: <span className="font-mono text-xs">{upgradeResult.subscription_id}</span>
              </p>
              <p className="text-[11px] text-gray-400 mb-5">{upgradeResult.message}</p>
              <button
                onClick={() => navigate('/parent-dashboard')}
                className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg"
                data-testid="console-upgrade-modal-dashboard"
              >Go to Parent Dashboard</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentConsole;
